package com.trevorwiebe.timesheet.punch.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.Util.localDateTime
import com.trevorwiebe.timesheet.core.domain.Util.toNearestFifteenMinutes
import com.trevorwiebe.timesheet.core.domain.model.Holiday
import com.trevorwiebe.timesheet.core.domain.model.Organization
import com.trevorwiebe.timesheet.core.domain.model.Punch
import com.trevorwiebe.timesheet.core.domain.model.PunchType
import com.trevorwiebe.timesheet.core.domain.model.Rate
import com.trevorwiebe.timesheet.core.domain.model.TimeSheet
import com.trevorwiebe.timesheet.core.domain.usecases.GetCurrentPayPeriodStartAndEnd
import com.trevorwiebe.timesheet.punch.domain.PunchRepository
import com.trevorwiebe.timesheet.punch.domain.usecases.AddUpHours
import com.trevorwiebe.timesheet.punch.domain.usecases.CalculateTimeSheets
import com.trevorwiebe.timesheet.punch.domain.usecases.ProcessPunchesForUi
import com.trevorwiebe.timesheet.punch.presentation.uiUtils.UiPunch
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.daysUntil
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime

@OptIn(ExperimentalTime::class)
class PunchViewModel(
    private val startDate: String?,
    private val endDate: String?,
    private val timeSheetId: String?,
    private val userId: String? = null,
    private val isAdmin: Boolean = false,
    private val punchRepository: PunchRepository,
    private val coreRepository: CoreRepository,
    private val calculateTimeSheets: CalculateTimeSheets,
    private val processPunchesForUi: ProcessPunchesForUi,
    private val addUpHours: AddUpHours,
    private val getCurrentPayPeriodStartAndEnd: GetCurrentPayPeriodStartAndEnd,
) : DisposableViewModel() {

    private val _staticPunchState = MutableStateFlow(StaticPunchState())
    val staticPunchState = _staticPunchState.asStateFlow()

    private val _dynamicPunchState = MutableStateFlow(DynamicPunchState())
    val dynamicPunchState = _dynamicPunchState.asStateFlow()

    private val _elementVisibilityState = MutableStateFlow(ElementVisibilityState())
    val elementVisibilityState = _elementVisibilityState.asStateFlow()

    fun getHoursWorked(
        currentDate: LocalDateTime,
        punches: Pair<List<UiPunch>, List<UiPunch>>?,
    ): List<Pair<String, Double>> {
        if(punches == null) return emptyList()
        val punchMap = mapOf(currentDate to punches)
        val punchList = punchMap.values.flatMap { it.first + it.second }
        val hours = addUpHours(
            punchList,
            _staticPunchState.value.rateList
        )
        return hours
    }

    init {
        _staticPunchState.update { it.copy(isAdmin = isAdmin) }
        getOrganization {

            // Calculate the current date list
            calculatePayPeriodDateList()

            getRates {
                val payPeriod = if (startDate == null && endDate == null) {
                    getCurrentPayPeriodStartAndEnd(
                        organization = _staticPunchState.value.organization
                            ?: throw Exception("Organization is null")
                    )
                } else {
                    LocalDateTime.parse(startDate!!) to
                            LocalDateTime.parse(endDate!!).date
                                .atTime(23, 59, 59)
                }
                _staticPunchState.update { it.copy(currentPeriod = payPeriod) }
                getPunches(
                    start = payPeriod.first,
                    end = payPeriod.second
                )
                if (timeSheetId != null) {
                    initiateGetTimeSheet(timeSheetId)
                }

                val currentYear = Clock.System.now()
                    .toLocalDateTime(TimeZone.currentSystemDefault())
                    .year
                    .toString()
                getHolidays(currentYear)
            }
        }
    }

    fun onEvent(event: PunchEvents) {
        when (event) {
            is PunchEvents.OnPunch -> {
                val lastPunchRateId = lastPunchIn()?.rateId ?:
                _staticPunchState.value.rateList
                    .firstOrNull { it.userFacing && it.defaultRate }?.id ?:
                    throw Exception("No default rate found")

                sendPunch(lastPunchRateId, event.punchType)
            }
            is PunchEvents.OnShowConfirmDeletePunchesSheet -> {
                _elementVisibilityState.update {
                    it.copy(showConfirmDeletePunchesSheetUiPunch = event.uiPunch)
                }
            }
            is PunchEvents.OnDeletePunches -> {
                val uiPunch = _elementVisibilityState.value.showConfirmDeletePunchesSheetUiPunch
                val punchIds = listOf(uiPunch?.punchIn?.punchId, uiPunch?.punchOut?.punchId)
                initiateDeletePunches(punchIds)
                _elementVisibilityState.update {
                    it.copy(showConfirmDeletePunchesSheetUiPunch = null)
                }
                val ptoId = Util.getRateIDFromName("PTO", _staticPunchState.value.rateList)
                val isPTO = uiPunch?.punchIn?.rateId.equals(ptoId)
                if (isPTO) {
                    viewModelScope.launch {
                        val response = adjustPTO(8)
                        if(!response.error.isNullOrEmpty()) {
                            _elementVisibilityState.update {
                                it.copy(
                                    showPtoErrorDialog = true,
                                    ptoError = response.error,
                                )
                            }
                        }
                    }
                }
            }
            is PunchEvents.OnShowAddHoursDialog -> {
                _elementVisibilityState.update { it.copy(showAddHourDialogTime = event.addHoursDialogTime) }
            }
            is PunchEvents.OnUpdatePunch -> {
                val dateTime = event.punch.dateTime.toNearestFifteenMinutes()
                updatePunch(event.punch.copy(dateTime = dateTime, reviewRequired = false))
            }
            is PunchEvents.OnAddHours -> {
                initiateAddingHours(event.punchIn, event.punchOut)
            }
            is PunchEvents.OnUpdateRate -> {
                initiateUpdatePunchesWithNewRate(
                    event.punchIn.copy(reviewRequired = false),
                    event.punchOut?.copy(reviewRequired = false)
                )
            }
            is PunchEvents.OnSetSubmitPayPeriodDialog -> {
                if (event.visible) {
                    val reviewRequired = _dynamicPunchState.value.punches.values
                        .flatMap { it.first + it.second }
                        .filter {
                            it.punchIn.reviewRequired == true ||
                            it.punchOut?.reviewRequired == true ||
                            it.punchInError != null ||
                            it.punchOutError != null
                        }
                        .sortedBy { it.punchIn.dateTime }
                    if (reviewRequired.isNotEmpty()) {
                        _elementVisibilityState.update { it.copy(reviewRequiredPunches = reviewRequired) }
                    } else {
                        _elementVisibilityState.update { it.copy(submitPayPeriodDialog = true) }
                    }
                } else {
                    _elementVisibilityState.update { it.copy(submitPayPeriodDialog = false) }
                }
            }
            is PunchEvents.OnDismissReviewRequiredDialog -> {
                _elementVisibilityState.update { it.copy(reviewRequiredPunches = emptyList()) }
            }

            is PunchEvents.OnConfirmPayPeriod -> {
                val timeSheet = _dynamicPunchState.value.timeSheet?.copy(
                    confirmedByUser = true
                )
                updateTimeSheet(timeSheet)
            }
            is PunchEvents.OnShowInfo -> {
                _elementVisibilityState.update {
                    it.copy(showPayPeriodInfoSheet = event.showInfoSheet)
                }
            }
            is PunchEvents.OnSetTimePickerVisibility -> {
                _dynamicPunchState.update {
                    it.copy(punchToEdit = event.punch)
                }
            }
            is PunchEvents.OnAddPTO -> {

                viewModelScope.launch {

                    val response = adjustPTO(-8)
                    if(response.error.isNullOrEmpty()) {

                        val date = event.date
                        val rateList = _staticPunchState.value.rateList
                        val ptoId = Util.getRateIDFromName("PTO", rateList)

                        val punchIn = Punch(
                            punchId = "",
                            dateTime = date.date.atTime(8,0),
                            rateId = ptoId,
                            reviewRequired = null,
                            type = PunchType.IN
                        )

                        val punchOut = Punch(
                            punchId = "",
                            dateTime = date.date.atTime(16, 0),
                            rateId = ptoId,
                            reviewRequired = null,
                            type = PunchType.OUT
                        )

                        initiateAddingHours(punchIn, punchOut)
                    }else{
                        _elementVisibilityState.update {
                            it.copy(
                                showPtoErrorDialog = true,
                                ptoError = response.error
                            )
                        }
                    }
                }
            }
            is PunchEvents.OnShowPTODialog -> {
                _elementVisibilityState.update { it.copy(showPtoErrorDialog = false) }
            }
        }
    }

    private fun calculatePayPeriodDateList() {
        val result = if (startDate == null && endDate == null) {
            calculateTimeSheets(
                organization = _staticPunchState.value.organization ?: return
            )
        } else {
            val startLocalDate = LocalDateTime.parse(startDate!!)
            val endLocalDate = LocalDateTime.parse(endDate!!)

            val result = mutableListOf<LocalDateTime>()

            // Calculate number of days between the dates
            val daysBetween = startLocalDate.date.daysUntil(endLocalDate.date)

            // Add all dates to the list
            for (i in 0..daysBetween) {
                result.add(
                    startLocalDate.date
                        .plus(i, DateTimeUnit.DAY)
                        .atTime(0, 0)
                )
            }
            result.toList().reversed()
        }
        _staticPunchState.update { it.copy(timeSheetDateList = result) }
    }

    private fun getOrganization(onReceived: () -> Unit) {
        viewModelScope.launch {
            val result = coreRepository.getOrganization()
            if (result.error.isNullOrEmpty()) {
                val organization = result.data as Organization
                _staticPunchState.update { it.copy(organization = organization) }
                onReceived()
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun getRates(received: () -> Unit = {}) {
        viewModelScope.launch {
            val result = punchRepository.getRates()
            if (result.error.isNullOrEmpty()) {
                val rates = result.data as List<Rate>
                _staticPunchState.update { it.copy(rateList = rates) }
                received()
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun getPunches(
        start: LocalDateTime,
        end: LocalDateTime
    ) {
        val job = viewModelScope.launch {
            punchRepository.getPunches(start, end, userId).collect { result ->
                if (result.error.isNullOrEmpty()) {
                    val punchList = result.data as List<Punch>
                    val datesList = _staticPunchState.value.timeSheetDateList
                    val rateList = _staticPunchState.value.rateList
                    initiatePunchProcessing(datesList, punchList, rateList)
                }
            }
        }
        FirestoreListenerRegistry.registerJob(job)
    }

    private fun sendPunch(rateId: String, punchType: PunchType) {

        _elementVisibilityState.update { it.copy(punchLoading = true) }

        val punch = Punch(
            punchId = "",
            dateTime = localDateTime().toNearestFifteenMinutes(),
            rateId = rateId,
            reviewRequired = null,
            type = punchType
        )

        viewModelScope.launch {
            punchRepository.addPunch(punch, userId)
            _elementVisibilityState.update { it.copy(punchLoading = false) }
        }
    }

    private fun initiatePunchProcessing(
        datesList: List<LocalDateTime>,
        punchList: List<Punch>,
        rateList: List<Rate>
    ) {
        val ptoRate = Util.getRateIDFromName("PTO", rateList)
        val holidayRate = Util.getRateIDFromName("Holiday", rateList)

        val mostRecentPunch = punchList
            .filter { it.rateId != ptoRate && it.rateId != holidayRate }
            .maxByOrNull { it.dateTime }
        if (mostRecentPunch != null && mostRecentPunch.dateTime > localDateTime()) {
            val message = if (mostRecentPunch.type == PunchType.IN) {
                "You have a clock in time that is ahead of the current time"
            } else {
                "You have a clock out time that is ahead of the current time"
            }
            _dynamicPunchState.update { it.copy(clockMessage = message) }
        } else {
            _dynamicPunchState.update { it.copy(clockMessage = null) }
        }
        val punchMap = processPunchesForUi(datesList, punchList, listOf(ptoRate, holidayRate))
        val punchList = punchMap.values.flatMap { it.first + it.second }
        val totalHours = addUpHours(punchList, rateList)
        _dynamicPunchState.update { it.copy(punches = punchMap) }
        _staticPunchState.update { it.copy(hoursMap = totalHours) }
    }

    private fun initiateDeletePunches(
        punchIds: List<String?>
    ) {
        viewModelScope.launch {
            punchRepository.deletePunches(punchIds, userId)
        }
    }

    private fun updatePunch(punch: Punch) {
        viewModelScope.launch {
            punchRepository.updatePunch(punch, userId)
        }
    }

    private fun initiateAddingHours(
        punchIn: Punch,
        punchOut: Punch
    ) {
        viewModelScope.launch {
            val response = punchRepository.addHours(punchIn, punchOut, userId)
            if (response.error.isNullOrEmpty()) {
                _elementVisibilityState.update { it.copy(showAddHourDialogTime = null) }
            }
        }
    }

    private fun initiateUpdatePunchesWithNewRate(
        punchIn: Punch,
        punchOut: Punch?
    ) {
        viewModelScope.launch {
            val response = punchRepository.updatePunchesWithNewRate(punchIn, punchOut, userId)
            if (!response.error.isNullOrEmpty()) {
                _elementVisibilityState.update {
                    it.copy(
                        showPtoErrorDialog = true,
                        ptoError = response.error
                    )
                }
            }
        }
    }

    private fun initiateGetTimeSheet(id: String) {
        val job = viewModelScope.launch {
            punchRepository.getTimeSheetById(id, userId).collect { result ->
                if (result.error.isNullOrEmpty()) {
                    val timeSheet = result.data as TimeSheet
                    _dynamicPunchState.update { it.copy(timeSheet = timeSheet) }
                }
            }
        }
        FirestoreListenerRegistry.registerJob(job)
    }

    private fun updateTimeSheet(timeSheet: TimeSheet?) {
        viewModelScope.launch {
            if (timeSheet != null) {
                val result = punchRepository.updateTimeSheet(timeSheet, userId)
                if (result.error.isNullOrEmpty()) {
                    _elementVisibilityState.update { it.copy(submitPayPeriodDialog = false) }
                }
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun getHolidays(year: String) {
        viewModelScope.launch {
            val result = coreRepository.getHolidays(year, "US")
            if (result.error.isNullOrEmpty()) {
                val holidays = result.data as List<Holiday>
                _staticPunchState.update { it.copy(holidays = holidays) }
            }
        }
    }

    private suspend fun adjustPTO(hours: Int): TSResult{
        return punchRepository.adjustVacationTimeByHours(hours, userId)
    }

    private fun lastPunchIn(): Punch? {
        val mutablePunchList = mutableListOf<UiPunch>()
        _dynamicPunchState.value.punches.values.forEach { punchList ->
            mutablePunchList.addAll(punchList.first)
            mutablePunchList.addAll(punchList.second)
        }
        return mutablePunchList
            .sortedByDescending { it.punchIn.dateTime }
            .firstOrNull { it.punchOut == null }?.punchIn
    }
}